import React, { useState } from "react";
import { Collapse } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import JsonCollapse from "../../../Components/JsonCollapse";
import { proxyAPI } from "../../../../proxyAPI";
import { selectInputArguments } from "../../../../store/slices/projectSlice";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";

interface Action {
  TypeUUID?: string;
  ExtractOptions?: {
    DelimiterStart?: string;
    DelimiterEnd?: string;
    CSV?: string[];
    IsMultiline?: boolean;
    IsMultiple?: boolean;
  };
}

interface TestExtractionProps {
  action: Action;
}

export default function TestExtraction({ action }: TestExtractionProps) {
  const [show, setShow] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const [result, setResult] = useState<any>("");
  const inputArguments = useSelector(selectInputArguments);
  const [loading, setLoading] = useState<boolean>(false);

  const extractionRules = {
    Label: inputArguments.find((arg: any) => arg.UUID === action.TypeUUID)?.Label,
    StartDelimiter: action.ExtractOptions?.DelimiterStart || "",
    EndDelimiters: [action.ExtractOptions?.DelimiterEnd || ""],
    CSV: action.ExtractOptions?.CSV || [],
    IsMultiline: action.ExtractOptions?.IsMultiline || false,
    IsMultiple: action.ExtractOptions?.IsMultiple || false,
  };

  const test = async () => {
    setLoading(true);
    const data = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `test-extract-variables`,
      method: "POST",
      body: {
        Source: text,
        ...extractionRules,
      },
    });
    setTimeout(() => {
      setLoading(false);
      setResult(data?.data);
    }, 500);
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-outline-secondary w-100"
        onClick={() => setShow((prev) => !prev)}
      >
        Test extraction
      </button>
      <Collapse
        in={show}
        dimension="width"
        style={{
          position: "absolute",
          top: 0,
          right: 480,
          height: "100vh",
          background: "white",
        }}
      >
        <div>
          <div className="p-3" style={{ width: 480 }}>
            <h5 className="mb-3">Test extraction</h5>
            <label className="form-label">Example text</label>
            <div style={{ position: "relative", height: 300 }}>
              {[
                {
                  highlightedText: extractionRules.StartDelimiter,
                  color: "#aaf",
                },
                {
                  highlightedText: extractionRules.EndDelimiters[0],
                  color: "#afa",
                },
                ...extractionRules.CSV.map((csv) => ({
                  highlightedText: csv,
                  color: "#faa",
                })),
              ].map((highlighter, i) => (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    backgroundColor: "transparent",
                    color: "transparent",
                    height: "100%",
                    fontFamily: "monospace",
                  }}
                  className="form-control"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: highlighter.highlightedText
                      ? text
                          .replace(/(\n|\r|\r\n)/g, "<br>")
                          .replace(
                            new RegExp(highlighter.highlightedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
                            `<span style="background:${highlighter.color}">${highlighter.highlightedText}</span>`
                          )
                      : text.replace(/(\n|\r|\r\n)/g, "<br>"),
                  }}
                />
              ))}

              <textarea
                style={{
                  position: "absolute",
                  height: "100%",
                  background: "transparent",
                  fontFamily: "monospace",
                }}
                placeholder="Example text for extraction"
                className="form-control mb-1"
                rows={10}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="btn btn-outline-primary w-100 mb-3"
              style={{ height: 38 }}
              onClick={test}
            >
              Test {loading && <FontAwesomeIcon icon={faRefresh} spin />}
            </button>
            {result && (
              <>
                <label className="form-label">Result</label>
                <div className="card card-body">
                  <pre>{JSON.stringify(result, null, 2)}</pre>
                </div>
              </>
            )}
          </div>
          <JsonCollapse json={extractionRules} />
        </div>
      </Collapse>
    </>
  );
}
